import type {
  AllFormElementsJson,
  FormElementConditionJson,
  FormElementJson,
  FormElementLogicJson
} from '../../../modules/edit/forms-conditional-logic';
import { isSatisfied } from './forms-conditional-logic-evaluators';

const allElements: {[key: string]: FormElement} = {};
const allElementValues: {[key: number]: string | CheckBoxValuesJson | null} = {};

interface CheckBoxValuesJson {
  checked: string[],
  other?: string,
}

class FieldGroup {
  element: HTMLElement;
  logic: FormElementLogicJson;
  condition: Condition;

  constructor(element: HTMLElement) {
    this.element = element;
    this.logic = JSON.parse(this.element.querySelector('.conditional-logic-json').innerHTML) as FormElementLogicJson;
    this.condition = new Condition(this.logic.condition);
    this.condition.getRelatedElementIds().forEach((id) => {
      allElements[id].addChangeListener(() => {
        this.update();
      });
    });

    this.update();
  }

  update() {
    const satisfied = isSatisfied(this.condition.json, FieldGroup.allElementsJson, allElementValues);
    const hide = (satisfied && this.logic.outcome.type === 'hide')
      || (!satisfied && this.logic.outcome.type === 'show');
    this.element.classList.toggle('hidden', hide);
  }

  static allElementsJson: AllFormElementsJson = { elements: []};
}

class FormElement {
  container: HTMLElement;
  input: HTMLInputElement | null; // Only applies to some element types
  isSimpleInput: boolean; // Is it just one thing that returns one value?
  json: FormElementJson;
  changeListeners: ((value: string | CheckBoxValuesJson | null) => void)[] = [];
  value: string | CheckBoxValuesJson | null = null;
  canHaveOther: boolean = false;
  hasComparableValues: boolean = false;

  constructor(json: FormElementJson) {
    this.json = json;
    this.container = document.querySelector<HTMLElement>(`[data-element-id="${json.id}"]`);
    this.canHaveOther = (json.type === 'checkboxElement' || json.type === 'radioElement');
    this.hasComparableValues = ( ['numberElement', 'dateTimeElement', 'paymentElement'].includes(json.type) );

    if (!this.container) {
      return; // No point in the rest of this if the element doesn't exist
    }

    switch (json.type) {
      case "textFieldElement":
      case "emailElement":
      case "phoneNumber":
      case "numberElement":
      case "dateTimeElement":
        this.input = this.container.querySelector<HTMLInputElement>('input');
        this.isSimpleInput = true;
        break;
      case "textBoxElement":
        this.input = this.container.querySelector<HTMLInputElement>('textarea');
        this.isSimpleInput = true;
        break;
      case "listElement":
        this.input = this.container.querySelector<HTMLInputElement>('select');
        this.isSimpleInput = true;
        break;
      case "paymentElement":
        this.input = this.container.querySelector<HTMLInputElement>('select');
        break;
      default:
        this.isSimpleInput = false;
    } // if it's not one of these, input will be handled in a special way

    if (this.input) {
      // Check against all kinds of stuff for maximum responsiveness. Focus and blur are mainly to handle date elements updated by JS
      ['focus', 'blur', 'change', 'keydown'].forEach(eventType => this.input.addEventListener(eventType, () => { this.change() }));
      if (json.type === 'paymentElement') {
        const otherInput = this.container.querySelector<HTMLInputElement>('input.payment-other');
        if (otherInput) {
          ['focus', 'blur', 'change', 'keydown'].forEach(eventType => otherInput.addEventListener(eventType, () => { this.change() }));
        }
      }
    } else {
      switch (json.type) {
        case "checkboxElement":
        case "radioElement":
          this.container.querySelectorAll('input').forEach((input) => {
            input.addEventListener('change', () => { this.change() })
          });
          break;
        case "pairList":
          this.container.querySelectorAll('select.pairDropdown').forEach((select) => {
            select.addEventListener('change', () => { this.change() });
          });
          break;
        case "attachment":
          this.observeFileAttachments();
          break;
      }

      // If we've got this far without setting up change observation then this is not a supported conditional
      // logic element and we can happily ignore it.
    }

    this.value = this.getCurrentValue();
    allElementValues[this.json.id] = this.value;
  }

  private observeFileAttachments() {
    const observer = new MutationObserver(() => {
      this.change();
    });
    observer.observe(this.container.querySelector('#attachmentResult'), { childList: true });
  }

  change() {
    this.value = this.getCurrentValue();
    allElementValues[this.json.id] = this.value;
    this.changeListeners.forEach((listener) => {
      listener(this.value);
    });
  }

  getCurrentValue(): string | CheckBoxValuesJson | null {
    if (!this.container) { return null; }

    // If it's a payment element, a select value of '' indicates that Other has been chosen.
    if (this.isSimpleInput || (this.json.type === 'paymentElement' && this.input.value !== '')) {
      return this.input.value;
    }

    switch (this.json.type) {
      case "checkboxElement":
      case "radioElement":
        return {
          checked: Array.from(this.container.querySelectorAll<HTMLInputElement>('input')).filter(i => i.checked).map(i => i.value),
          other: this.container.querySelector<HTMLInputElement>('input[type="text"]')?.value
        }
      case "pairList":
        return this.getPairListValue();
      case "attachment":
        if (this.container.querySelector('[data-file-name]')) {
          return "has-attachment"
        } else {
          return '';
        }
      case 'paymentElement':
        // User must have selected Other or it would this would have returned above...
        return this.container.querySelector<HTMLInputElement>('input.payment-other').value;
    }

    return null;
  }

  private getPairListValue(): string {
    const inputs = this.container.querySelectorAll<HTMLSelectElement>('select');
    return `${inputs[0].value}${inputs[1].value}`;
  }

  addChangeListener(fn: (value: string | CheckBoxValuesJson | null) => void) {
    this.changeListeners.push(fn);
  }
}

class Condition {
  targetElement: FormElement;
  json: FormElementConditionJson;
  conditions: Condition[];
  // Flag used when something is wrong like the targetElement doesn't exist or whatever
  private ignore: boolean = false;

  constructor(json: FormElementConditionJson) {
    this.json = json;
    this.targetElement = allElements[this.json.targetElementId];
    if (!this.targetElement && !(this.json.type === 'and' || this.json.type === 'or')) {
      this.ignore = true;
      return;
    } // Element may have been deleted, ignore this condition
    if ((this.json.conditions?.length ?? 0) > 0) {
      this.conditions = this.json.conditions.map((c) => new Condition(c));
    }
  }

  getRelatedElementIds(): number[] {
    if (this.ignore) { return [] }
    if (this.json.type === 'and' || this.json.type === 'or') {
      return this.conditions?.flatMap((c) => c.getRelatedElementIds()) ?? [];
    } else {
      return [this.targetElement.json.id];
    }
  }
}

export function init() {
  if (!document.querySelector('.conditional-logic-json') || !document.getElementById('form-elements-json')) {
    return; // There is no logic in this place
  }

  const elementsJson = JSON.parse(document.getElementById('form-elements-json').innerHTML) as AllFormElementsJson;
  elementsJson.elements.forEach((e) => allElements[e.id] = new FormElement(e));

  const fieldgroups = document.querySelectorAll<HTMLElement>('.fieldgroup');
  if (fieldgroups.length === 0) {
    return; // No fieldgroups to apply logic to
  }

  FieldGroup.allElementsJson = elementsJson;
  fieldgroups.forEach((f) => new FieldGroup(f));
}
